/**
 * Gebruikersbeheer - Formulier voor aanmaken/bewerken gebruikers
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usersService, UserCreate, Role } from '../../services/users.service';
import { werknemersService, Werknemer } from '../../services/werknemers.service';
import ErrorDialog from '../../components/ErrorDialog';
import './Settings.css';

const UsersForm = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const isEditMode = !!userId;

  const [roles, setRoles] = useState<Role[]>([]);
  const [werknemers, setWerknemers] = useState<Werknemer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    voornaam: '',
    achternaam: '',
    straat: '',
    huisnummer: '',
    postcode: '',
    plaats: '',
    land: 'Nederland',
    telefoon: '',
    mobiel: '',
    rol_id: undefined as number | undefined,
    werknemer_id: undefined as number | undefined,
    is_active: true,
    is_superuser: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [werknemerZoek, setWerknemerZoek] = useState('');
  const [werknemerDropdownOpen, setWerknemerDropdownOpen] = useState(false);

  const loadRoles = useCallback(async () => {
    try {
      const rolesData = await usersService.getRoles();
      setRoles(rolesData);
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen van rollen');
    }
  }, []);

  const loadWerknemers = useCallback(async () => {
    try {
      const werknemersData = await werknemersService.getAll({ limit: 100, actief: true });
      setWerknemers(werknemersData.items);
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen van werknemers');
    }
  }, []);

  const loadUser = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const user = await usersService.getById(parseInt(userId));
      setFormData({
        username: user.username,
        email: user.email,
        password: '', // Never load password
        voornaam: user.voornaam || '',
        achternaam: user.achternaam || '',
        straat: user.straat || '',
        huisnummer: user.huisnummer || '',
        postcode: user.postcode || '',
        plaats: user.plaats || '',
        land: user.land || 'Nederland',
        telefoon: user.telefoon || '',
        mobiel: user.mobiel || '',
        rol_id: user.rol_id,
        werknemer_id: user.werknemer_id,
        is_active: user.is_active,
        is_superuser: user.is_superuser,
      });
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen gebruiker');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadRoles();
    loadWerknemers();
    if (isEditMode) {
      loadUser();
    }
  }, [isEditMode, loadRoles, loadWerknemers, loadUser]);

  // Update werknemerZoek wanneer werknemer_id wijzigt
  useEffect(() => {
    if (formData.werknemer_id && werknemers.length > 0) {
      const werknemer = werknemers.find(w => w.id === formData.werknemer_id);
      if (werknemer) {
        const naam = [werknemer.voornaam, werknemer.tussenvoegsel, werknemer.achternaam].filter(Boolean).join(' ');
        setWerknemerZoek(naam + (werknemer.functie ? ` (${werknemer.functie})` : ''));
      }
    } else if (!formData.werknemer_id) {
      setWerknemerZoek('');
    }
  }, [formData.werknemer_id, werknemers]);

  // Click outside handler om dropdown te sluiten
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-select')) {
        setWerknemerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'rol_id') {
      setFormData(prev => ({
        ...prev,
        rol_id: value ? parseInt(value) : undefined
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleWerknemerSelect = (werknemer: Werknemer | null) => {
    if (werknemer) {
      setFormData(prev => ({
        ...prev,
        werknemer_id: werknemer.id,
        email: werknemer.email || prev.email,
        voornaam: werknemer.voornaam || prev.voornaam,
        achternaam: werknemer.achternaam || prev.achternaam,
        straat: werknemer.straat || prev.straat,
        huisnummer: werknemer.huisnummer || prev.huisnummer,
        postcode: werknemer.postcode || prev.postcode,
        plaats: werknemer.plaats || prev.plaats,
        land: werknemer.land || prev.land,
        telefoon: werknemer.telefoon || prev.telefoon,
        mobiel: werknemer.mobiel || prev.mobiel,
      }));
      setWerknemerDropdownOpen(false);
    } else {
      // Handmatig invoeren (geen koppeling)
      setFormData(prev => ({
        ...prev,
        werknemer_id: undefined,
      }));
      setWerknemerZoek('');
      setWerknemerDropdownOpen(false);
    }
  };

  // Gefilterde werknemers
  const filteredWerknemers = werknemers.filter(werknemer => {
    const searchTerm = werknemerZoek.toLowerCase();
    const voornaam = werknemer.voornaam?.toLowerCase() || '';
    const achternaam = werknemer.achternaam?.toLowerCase() || '';
    const functie = werknemer.functie?.toLowerCase() || '';
    return voornaam.includes(searchTerm) || achternaam.includes(searchTerm) || functie.includes(searchTerm);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.username || !formData.email) {
      setError('Gebruikersnaam en email zijn verplicht');
      return;
    }

    if (!isEditMode && !formData.password) {
      setError('Wachtwoord is verplicht voor nieuwe gebruikers');
      return;
    }

    if (formData.password && formData.password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens zijn');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode && userId) {
        // Update user - only send password if changed
        const updateData: any = {
          username: formData.username,
          email: formData.email,
          voornaam: formData.voornaam,
          achternaam: formData.achternaam,
          straat: formData.straat,
          huisnummer: formData.huisnummer,
          postcode: formData.postcode,
          plaats: formData.plaats,
          land: formData.land,
          telefoon: formData.telefoon,
          mobiel: formData.mobiel,
          rol_id: formData.rol_id,
          werknemer_id: formData.werknemer_id,
          is_active: formData.is_active,
          is_superuser: formData.is_superuser,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        await usersService.update(parseInt(userId), updateData);
      } else {
        // Create new user
        const createData: UserCreate = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          rol_id: formData.rol_id,
          is_active: formData.is_active,
          is_superuser: formData.is_superuser,
        };

        await usersService.create(createData);
      }

      navigate('/settings/users');
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isEditMode ? 'Gebruiker Bewerken' : 'Nieuwe Gebruiker'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-container" autoComplete="off">
        <div className="form-section">
          <h2>Gebruikersgegevens</h2>

          <div className="form-group">
            <label htmlFor="werknemer_zoek">Koppel aan werknemer (optioneel)</label>
            <div className="searchable-select">
              <input
                type="text"
                id="werknemer_zoek"
                placeholder="Zoek op naam of functie..."
                value={werknemerZoek}
                onChange={(e) => {
                  setWerknemerZoek(e.target.value);
                  setWerknemerDropdownOpen(true);
                }}
                onFocus={() => setWerknemerDropdownOpen(true)}
                autoComplete="off"
              />
              {werknemerDropdownOpen && (
                <div className="dropdown-list">
                  <div
                    className={`dropdown-item ${!formData.werknemer_id ? 'selected' : ''}`}
                    onClick={() => handleWerknemerSelect(null)}
                  >
                    Handmatig invoeren (geen koppeling)
                  </div>
                  {filteredWerknemers.length > 0 ? (
                    filteredWerknemers.map(werknemer => {
                      const naam = [werknemer.voornaam, werknemer.tussenvoegsel, werknemer.achternaam].filter(Boolean).join(' ');
                      return (
                        <div
                          key={werknemer.id}
                          className={`dropdown-item ${formData.werknemer_id === werknemer.id ? 'selected' : ''}`}
                          onClick={() => handleWerknemerSelect(werknemer)}
                        >
                          {naam} {werknemer.functie ? `(${werknemer.functie})` : ''}
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown-item disabled">Geen werknemers gevonden</div>
                  )}
                </div>
              )}
            </div>
            <small className="form-help">
              Zoek en selecteer een werknemer om gegevens automatisch in te vullen
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="username">Gebruikersnaam *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              minLength={3}
              maxLength={100}
              placeholder="Gebruikersnaam"
              autoComplete="new-username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Email adres"
              autoComplete="new-email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="voornaam">Voornaam</label>
            <input
              type="text"
              id="voornaam"
              name="voornaam"
              value={formData.voornaam}
              onChange={handleChange}
              maxLength={100}
              placeholder="Voornaam"
            />
          </div>

          <div className="form-group">
            <label htmlFor="achternaam">Achternaam</label>
            <input
              type="text"
              id="achternaam"
              name="achternaam"
              value={formData.achternaam}
              onChange={handleChange}
              maxLength={100}
              placeholder="Achternaam"
            />
          </div>

          <div className="form-group">
            <label htmlFor="straat">Straat</label>
            <input
              type="text"
              id="straat"
              name="straat"
              value={formData.straat}
              onChange={handleChange}
              maxLength={255}
              placeholder="Straatnaam"
            />
          </div>

          <div className="form-group">
            <label htmlFor="huisnummer">Huisnummer</label>
            <input
              type="text"
              id="huisnummer"
              name="huisnummer"
              value={formData.huisnummer}
              onChange={handleChange}
              maxLength={20}
              placeholder="123"
            />
          </div>

          <div className="form-group">
            <label htmlFor="postcode">Postcode</label>
            <input
              type="text"
              id="postcode"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              maxLength={20}
              placeholder="1234 AB"
            />
          </div>

          <div className="form-group">
            <label htmlFor="plaats">Plaats</label>
            <input
              type="text"
              id="plaats"
              name="plaats"
              value={formData.plaats}
              onChange={handleChange}
              maxLength={100}
              placeholder="Plaatsnaam"
            />
          </div>

          <div className="form-group">
            <label htmlFor="land">Land</label>
            <input
              type="text"
              id="land"
              name="land"
              value={formData.land}
              onChange={handleChange}
              maxLength={100}
              placeholder="Nederland"
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefoon">Telefoon</label>
            <input
              type="tel"
              id="telefoon"
              name="telefoon"
              value={formData.telefoon}
              onChange={handleChange}
              maxLength={50}
              placeholder="+31 20 1234567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobiel">Mobiel</label>
            <input
              type="tel"
              id="mobiel"
              name="mobiel"
              value={formData.mobiel}
              onChange={handleChange}
              maxLength={50}
              placeholder="+31 6 12345678"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Wachtwoord {isEditMode ? '(laat leeg om niet te wijzigen)' : '*'}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!isEditMode}
                minLength={8}
                placeholder="Minimaal 8 tekens"
                style={{ paddingRight: '3rem' }}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  color: '#6c757d',
                  fontSize: '1rem'
                }}
                title={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <small className="form-help">
              Minimaal 8 tekens
            </small>
          </div>
        </div>

        <div className="form-section">
          <h2>Rol en Rechten</h2>

          <div className="form-group">
            <label htmlFor="rol_id">Rol</label>
            <select
              id="rol_id"
              name="rol_id"
              value={formData.rol_id || ''}
              onChange={handleChange}
            >
              <option value="">Geen rol (beperkte toegang)</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.naam} - {role.omschrijving}
                </option>
              ))}
            </select>
            <small className="form-help">
              Kies de rol die de rechten van deze gebruiker bepaalt
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_superuser"
                checked={formData.is_superuser}
                onChange={handleChange}
              />
              <span>Superuser (volledige toegang, overschrijft rol)</span>
            </label>
            <small className="form-help">
              Superusers hebben altijd volledige toegang, ongeacht hun rol
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <span>Actief</span>
            </label>
            <small className="form-help">
              Inactieve gebruikers kunnen niet inloggen
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-lg btn-secondary"
            onClick={() => navigate('/settings/users')}
            disabled={loading}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="btn-lg btn-success"
            disabled={loading}
          >
            {loading ? 'Bezig...' : (isEditMode ? 'Opslaan' : 'Aanmaken')}
          </button>
        </div>
      </form>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default UsersForm;